import React, { useEffect, useRef, useState } from 'react';

import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import { AttentionRow, ownership, stepLabel } from './AttentionList';
import PositionPill from './PositionPill';
import { UI, cardSx, tableSx, value } from './operationalUi';

interface Props {
  rows: AttentionRow[];
  status: string;
  sort: string;
  comparability: string;
  onStatus: (v: string) => void;
  onComparability: (v: string) => void;
  onCampaign: (id: string) => void;
  onExport: () => void;
  reasons?: Record<string, number>;
  reasonMargins?: Record<string, number>;
  counts: { total: number; comparable: number; uncovered: number };
}
export default function CampaignsTable({
  rows,
  status,
  sort,
  comparability,
  onStatus,
  onComparability,
  onCampaign,
  onExport,
  counts,
  reasons = {},
  reasonMargins = {},
}: Props) {
  const [page, setPage] = useState(0);
  const container = useRef<HTMLDivElement>(null),
    [perPage, setPerPage] = useState(8);
  useEffect(() => {
    const fit = () => {
      if (container.current) setPerPage(Math.max(1, Math.floor((container.current.clientHeight - 42) / 48)));
    };
    fit();
    const observer = new ResizeObserver(fit);
    if (container.current) observer.observe(container.current);
    return () => observer.disconnect();
  }, []);
  const visiblePage = Math.min(page, Math.max(0, Math.ceil(rows.length / perPage) - 1));
  return (
    <Box sx={{ ...cardSx, height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Stack direction="row" flexWrap="wrap" gap={1}>
          <ToggleButtonGroup
            value={status}
            exclusive
            size="small"
            onChange={(_, v) => v && onStatus(v)}
            aria-label="Campaign status"
          >
            <ToggleButton value="live">Live</ToggleButton>
            <ToggleButton value="completed">Completed</ToggleButton>
            <ToggleButton value="all">All statuses</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={comparability}
            exclusive
            size="small"
            onChange={(_, v) => v && onComparability(v)}
            aria-label="Peer coverage"
          >
            <ToggleButton value="comparable">Comparable {counts.comparable}</ToggleButton>
            <ToggleButton value="uncovered">No peer group {counts.uncovered}</ToggleButton>
            <ToggleButton value="all">All {counts.total}</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Button size="small" onClick={onExport}>
          Export CSV
        </Button>
      </Stack>
      <Typography sx={{ fontSize: 12, color: UI.muted, mb: 1 }}>
        Sorted by{' '}
        {sort === 'margin'
          ? 'margin to date'
          : sort === 'name'
            ? 'campaign name'
            : sort === 'age'
              ? 'campaign age'
              : 'gap to peer pace, largest shortfall first'}
        .
      </Typography>
      <TableContainer
        ref={container}
        sx={{ flex: 1, minHeight: 0, overflow: 'auto', borderBottom: `1px solid ${UI.line}` }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            ...tableSx,
            tableLayout: 'fixed',
            minWidth: 1180,
            '& .MuiTableCell-root': { fontSize: 13, py: 0.5, px: 0.75, lineHeight: 1.25, borderColor: UI.line },
          }}
          aria-label="Flash campaigns"
        >
          <TableHead>
            <TableRow>
              {[
                '#',
                'Campaign',
                'Destination',
                'Territory',
                'PCM / owner',
                'Day',
                'Days left',
                'Packages/day',
                'Margin/day',
                'Weakest step',
                'v peer pace, 28d',
                'Peers',
              ].map((h, i) => (
                <TableCell
                  key={h}
                  sx={{
                    width: [25, 180, 80, 95, 125, 65, 45, 120, 160, 90, 105, 55][i],
                    whiteSpace: 'normal!important',
                  }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.slice(visiblePage * perPage, (visiblePage + 1) * perPage).map((row, i) => (
              <TableRow
                hover
                key={row.id}
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onCampaign(row.id)}
                onClick={() => onCampaign(row.id)}
                sx={{ cursor: 'pointer', height: 48 }}
              >
                <TableCell>{visiblePage * perPage + i + 1}</TableCell>
                <TableCell>
                  <Typography
                    title={row.name}
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {row.name}
                  </Typography>
                  {!row.comparable && (
                    <Typography sx={{ fontSize: 12, color: UI.muted }}>{row.reason?.replace(/_/g, ' ')}</Typography>
                  )}
                </TableCell>
                <TableCell title={row.destination} sx={{ whiteSpace: 'normal', overflowWrap: 'break-word' }}>
                  {row.destination}
                </TableCell>
                <TableCell
                  title={row.territory}
                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {row.territory}
                </TableCell>
                <TableCell
                  title={ownership(row)}
                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {ownership(row)}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {row.ageDays} of {row.durationDays}
                </TableCell>
                <TableCell>{row.daysLeft}</TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{ fontSize: 13 }}>{value(row.actual.packages_per_day)}</Typography>
                    <PositionPill
                      compact
                      position={row.positions.packages_per_day}
                      label={
                        row.tooEarly
                          ? 'Early'
                          : { below: 'Below', in_line: 'In line', above: 'Above', unscored: 'No peers' }[
                              row.positions.packages_per_day
                            ]
                      }
                    />
                  </Stack>
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{ fontSize: 13 }}>{value(row.actual.margin_per_day, 'currency')}</Typography>
                    <PositionPill
                      compact
                      position={row.positions.margin_per_day}
                      label={
                        row.zeroSale
                          ? 'No sales recorded'
                          : row.tooEarly
                            ? 'Early'
                            : { below: 'Below', in_line: 'In line', above: 'Above', unscored: 'No peers' }[
                                row.positions.margin_per_day
                              ]
                      }
                    />
                  </Stack>
                  <Typography sx={{ fontSize: 12, color: UI.muted }}>
                    med {value(row.peerMedian.margin_per_day, 'currency')}
                  </Typography>
                </TableCell>
                <TableCell
                  title={stepLabel(row.weakestStep)}
                  sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {stepLabel(row.weakestStep)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    whiteSpace: 'nowrap',
                    color: !row.tooEarly && row.positions.margin_per_day === 'below' ? UI.below.color : UI.muted,
                  }}
                >
                  {value(row.gap, 'currency')}
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {row.n || '—'}
                  {row.level === 'country' && <Typography sx={{ fontSize: 12, color: UI.teal }}>country</Typography>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!rows.length && (
          <Typography sx={{ p: 4, textAlign: 'center', color: UI.muted }}>
            No campaigns match these filters. Clear filters to widen the scope.
          </Typography>
        )}
      </TableContainer>
      {comparability !== 'comparable' && (
        <Box sx={{ borderTop: `1px solid ${UI.line}`, flexShrink: 0 }}>
          {[
            ['unknown_destination', 'Unknown destination · fix in Salesforce'],
            ['unknown_country', 'Unknown country'],
            ['too_few_peers', 'Fewer than 5 destination or country peers'],
            ['no_sales_rows', 'No sales records · verify coverage'],
          ]
            .filter(([key]) => (reasons[key] || 0) > 0)
            .map(([key, label]) => (
              <Stack
                key={key}
                direction="row"
                flexWrap="wrap"
                gap={0.5}
                justifyContent="space-between"
                sx={{ px: 1, py: 0.5, borderBottom: `1px solid ${UI.line}`, bgcolor: UI.sand }}
              >
                <Typography sx={{ fontSize: 12, color: UI.muted }}>
                  {reasons[key]} · {label}
                </Typography>
                <Typography sx={{ fontSize: 12, color: UI.muted }}>
                  {value(reasonMargins[key], 'currency')} margin to date
                </Typography>
              </Stack>
            ))}
        </Box>
      )}
      <TablePagination
        component="div"
        count={rows.length}
        page={visiblePage}
        rowsPerPage={perPage}
        rowsPerPageOptions={[perPage]}
        onPageChange={(_, v) => setPage(v)}
        sx={{ flexShrink: 0, '& .MuiTablePagination-toolbar': { minHeight: 42 } }}
      />
    </Box>
  );
}
